import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Slide,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import { useState, forwardRef, useEffect } from "react";
import api from "../../../api";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Slide transition for dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AA336A"];

const EndOfReport = () => {
  const theme = useTheme();
  const [selectedStudent, setSelectedStudent] = useState("");
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentModalOpen, setStudentModalOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [tabSubjects, setTabSubjects] = useState(["Summary"]);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch current user's (parent's) students
  useEffect(() => {
    const fetchStudents = async () => {
      setStudentsLoading(true);
      try {
        const response = await api.get("parent/students/names");
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          setStudents(list);
          if (list.length === 1) {
            setSelectedStudent(list[0].id);
            setStudentModalOpen(false);
          }
        } else {
          setStudents([]);
        }
      } catch (err) {
        console.error("Error fetching parent students:", err);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchStudents();
  }, []);

  // Fetch weekly performance when student is selected
  useEffect(() => {
    if (!selectedStudent) {
      setReportData(null);
      setTabSubjects(["Summary"]);
      setActiveTab(0);
      return;
    }
    const fetchWeeklyPerformance = async () => {
      setLoading(true);
      try {
        const response = await api.get(`parent/student/${selectedStudent}/weekly-performance`);
        if (response.data?.success && response.data?.data) {
          const { subjects = [], current_week = 1 } = response.data.data;
          setReportData(response.data.data);
          const subjectNames = subjects.map((s) => s.name).filter(Boolean);
          setTabSubjects(["Summary", ...subjectNames]);
          setActiveTab(0);
        } else {
          setReportData(null);
          setTabSubjects(["Summary"]);
        }
      } catch (err) {
        console.error("Error fetching weekly performance:", err);
        setReportData(null);
        setTabSubjects(["Summary"]);
      } finally {
        setLoading(false);
      }
    };
    fetchWeeklyPerformance();
  }, [selectedStudent]);

  const currentSubject = tabSubjects[activeTab];
  const subjectFromApi = reportData?.subjects?.find((s) => s.name === currentSubject);
  const subjectDataRaw = subjectFromApi?.topic_wise || [];
  const subjectData = subjectDataRaw.map((t) => ({
    topic: t.topic,
    week_number: t.week_number,
    score: Math.round(t.score),
    total: t.total ?? 100,
    attempts: t.attempts ?? 0,
  }));
  const subjectDataGroupedByTopic = subjectData.reduce((acc, t) => {
    const key = t.topic;
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const subjectDataForPie = Object.entries(subjectDataGroupedByTopic).map(([topic, items]) => {
    const avgScore = items.length ? Math.round(items.reduce((s, i) => s + i.score, 0) / items.length) : 0;
    const totalAttempts = items.reduce((s, i) => s + i.attempts, 0);
    return { topic, score: avgScore, total: 100, attempts: totalAttempts };
  });
  const weeklyData = (() => {
    const maxWeek = reportData?.current_week || 1;
    const scoreByWeek = Object.fromEntries(
      (subjectFromApi?.weekly_performance || []).map((w) => [
        Number(w.week_number),
        Number(w.score) ?? 0,
      ])
    );
    return Array.from({ length: maxWeek }, (_, i) => {
      const w = i + 1;
      return {
        week: `Week ${w}`,
        week_number: w,
        score: scoreByWeek[w] ?? 0,
      };
    });
  })();

  // Summary line chart data: all subjects, every week up to current
  const summaryChartData = (() => {
    if (!reportData?.subjects?.length || !reportData?.current_week) return [];
    const maxWeek = reportData.current_week;
    const scoreBySubjectByWeek = {};
    reportData.subjects.forEach((s) => {
      (s.weekly_performance || []).forEach((w) => {
        const wn = Number(w.week_number);
        if (!scoreBySubjectByWeek[wn]) scoreBySubjectByWeek[wn] = {};
        scoreBySubjectByWeek[wn][s.name] = Number(w.score) ?? 0;
      });
    });
    return Array.from({ length: maxWeek }, (_, i) => {
      const w = i + 1;
      const row = { week_number: w, week: `Week ${w}` };
      reportData.subjects.forEach((s) => {
        row[s.name] = scoreBySubjectByWeek[w]?.[s.name] ?? 0;
      });
      return row;
    });
  })();

  const handleStudentSelect = () => {
    if (selectedStudent) {
      setStudentModalOpen(false);
    }
  };

  return (
    <Box p={4} sx={{ backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      {/* Student Selector Modal - only when parent has more than one student */}
      <Dialog
        open={students.length > 1 && studentModalOpen}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          if (selectedStudent) setStudentModalOpen(false);
        }}
      >
        <DialogTitle>Select Student</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Student</InputLabel>
            <Select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              label="Select Student"
              disabled={studentsLoading}
            >
              {studentsLoading ? (
                <MenuItem disabled>Loading students...</MenuItem>
              ) : students.length === 0 ? (
                <MenuItem disabled>No students assigned</MenuItem>
              ) : (
                students.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.full_name || s.email || `Student ${s.id}`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Box mt={3} textAlign="right">
            <Button
              variant="contained"
              onClick={handleStudentSelect}
              disabled={!selectedStudent}
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Header with title and change student button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        {loading ? (
          <Skeleton variant="text" width={350} height={40} />
        ) : (
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Weekly Report Card - {selectedStudent ? (students.find((s) => s.id === selectedStudent)?.full_name || "Student") : "N/A"}
          </Typography>
        )}

        {students.length > 1 && (loading ? (
          <Skeleton variant="rectangular" width={120} height={36} />
        ) : (
          <Button
            variant="outlined"
            onClick={() => setStudentModalOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Change Student
          </Button>
        ))}
      </Box>

      {/* Linear Progress Bar */}
      {loading && (
        <Box mb={3}>
          <LinearProgress />
        </Box>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 4,
          borderBottom: "1px solid #ddd",
          ".MuiTab-root": {
            fontWeight: 500,
            textTransform: "capitalize",
            fontSize: "1rem",
          },
        }}
        disabled={loading}
      >
        {tabSubjects.map((label, idx) => (
          <Tab key={idx} label={label} />
        ))}
      </Tabs>

      {/* Content */}
      {loading ? (
        <Box>
          <Skeleton variant="rectangular" height={300} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={300} />
        </Box>
      ) : currentSubject !== "Summary" ? (
        <>
          <Typography variant="h5" mb={1} fontWeight={600}>
            {currentSubject} - Topic Wise Performance
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            For each topic: Week No. = curriculum week; Score = percentage achieved; Attempts = number of test attempts.
          </Typography>

          <Grid container spacing={3}>
            {Object.entries(subjectDataGroupedByTopic).map(([topicName, items], idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-5px)" },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {topicName}
                    </Typography>
                    {items
                      .sort((a, b) => (a.week_number ?? 0) - (b.week_number ?? 0))
                      .map((item, i) => (
                        <Box key={i} sx={{ mb: i < items.length - 1 ? 1.5 : 0 }}>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: "text.secondary" }}>Week No.: </Box>
                            <Box component="span" fontWeight={600}>{item.week_number ?? "N/A"}</Box>
                            <Box component="span" sx={{ color: "text.secondary", mx: 1 }}> • </Box>
                            <Box component="span" sx={{ color: "text.secondary" }}>Score: </Box>
                            <Box component="span" fontWeight={600} sx={{ color: "success.main" }}>{item.score}%</Box>
                            <Box component="span" sx={{ color: "text.secondary", mx: 1 }}> • </Box>
                            <Box component="span" sx={{ color: "text.secondary" }}>Attempts: </Box>
                            <Box component="span" fontWeight={600}>{item.attempts}</Box>
                          </Typography>
                        </Box>
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={4} mt={4}>
            <Grid item xs={12} md={6}>
              <Box
                p={2}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
                  height: "100%",
                }}
              >
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Weekly Performance Graph
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={weeklyData} margin={{ top: 20, right: 20, left: 25, bottom: 35 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 12 }}
                      label={{ value: "Week", position: "insideBottom", offset: -10 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      label={{ value: "Score", angle: -90, position: "insideLeft", offset: 10 }}
                    />
                    <Tooltip labelFormatter={(label) => (label ? `Week : ${String(label).replace(/^Week\s*/i, "")}` : label)} />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#3f51b5"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                p={2}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
                  height: "100%",
                }}
              >
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Topic-wise Score Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={subjectDataForPie.length > 0 ? subjectDataForPie : [{ topic: "No data", score: 1 }]}
                      dataKey="score"
                      nameKey="topic"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      label={({ name, percent }) =>
                        subjectDataForPie.length > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ""
                      }
                    >
                      {subjectDataForPie.length > 0 ? (
                        subjectDataForPie.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))
                      ) : (
                        <Cell key="empty" fill="rgba(0,0,0,0.08)" stroke="rgba(0,0,0,0.12)" strokeWidth={1} />
                      )}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>

          {subjectData.length === 0 && weeklyData.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No performance data yet for {currentSubject}
            </Typography>
          )}
        </>
      ) : (
        <Box mt={2}>
          <Typography variant="h5" mb={3} fontWeight={600}>
            Summary - All Subjects Weekly Performance
          </Typography>
          {reportData?.subjects?.length > 0 && summaryChartData.length > 0 ? (
            <Box
              p={2}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 3,
                boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
              }}
            >
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={summaryChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="week_number"
                    type="category"
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `Week ${v}`}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} label={{ value: "Score", angle: -90, position: "insideLeft" }} />
                  <Tooltip labelFormatter={(v) => `Week : ${v}`} />
                  {reportData.subjects.map((subject, index) => (
                    <Line
                      key={subject.id || index}
                      type="monotone"
                      dataKey={subject.name}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2, justifyContent: "center" }}>
                {reportData.subjects.map((subject, index) => (
                  <Box key={subject.id || index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 16, height: 4, bgcolor: COLORS[index % COLORS.length], borderRadius: 1 }} />
                    <Typography variant="body2">{subject.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : reportData?.subjects?.length > 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No weekly performance data yet.
            </Typography>
          ) : selectedStudent ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No subjects or performance data found for this student.
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary">
              Select a student to view their weekly performance summary.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EndOfReport;
